import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

/*
  Iter 85.64 · Team Member editor / creator.
  ──────────────────────────────────────────
  Single dialog used for both "add" and "edit" flows on the Team page.
    · When `member` is null → creates via POST /api/users (partner-only).
    · When `member` is a user doc → edits via PUT /api/users/{id}.

  Fields:
    · name                     (any authenticated user, on self)
    · capacity_hours_per_week  (any authenticated user, on self)
    · email + role             (partner-only, on any record — server enforces)

  The temp password field is only shown on create; the invitee rotates it
  via Settings → Change password. We never re-set passwords from here.
*/
const ROLES = ["Managing Principal", "Principal", "Partner", "Senior Associate", "Associate", "Analyst", "Advisor", "Operations"];

export default function TeamMemberDialog({
  open, onOpenChange, member, currentUser, onSaved,
}) {
  const isEdit = !!member;
  const isSelf = isEdit && member?.id === currentUser?.id;
  const canManage = (() => {
    const r = (currentUser?.role || "").toLowerCase().replace("_", " ");
    return r === "partner" || r === "managing principal";
  })();
  // Iter 85.75 · Self-edits ALWAYS unlock Role + Email. Backend has been
  // relaxed correspondingly: self-role and self-email changes on the
  // current user's own record are unconditionally allowed. Editing
  // other users' role/email still requires partner-tier.
  const canEditOwnRole = isSelf || canManage;
  const canEditOwnEmail = isSelf || canManage;

  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "Associate",
    capacity_hours_per_week: 40,
    password: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (member) {
      setForm({
        name: member.name || "",
        email: member.email || "",
        role: member.role || "Associate",
        capacity_hours_per_week: member.capacity_hours_per_week ?? 40,
        password: "",
      });
    } else {
      setForm({ name: "", email: "", role: "Associate", capacity_hours_per_week: 40, password: "" });
    }
  }, [open, member]);

  const commit = async () => {
    if (saving) return;
    const name = form.name.trim();
    if (!name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (!isEdit) {
        // Create — partner-only surface enforced server-side too.
        if (!form.email.trim()) { toast.error("Email required"); setSaving(false); return; }
        if (!form.password || form.password.length < 8) {
          toast.error("Temp password must be at least 8 characters"); setSaving(false); return;
        }
        const { data } = await api.post("/users", {
          name,
          email: form.email.trim().toLowerCase(),
          role: form.role,
          capacity_hours_per_week: Number(form.capacity_hours_per_week) || 40,
          password: form.password,
        });
        toast.success(`Added ${data.name}`);
        onOpenChange(false);
        onSaved?.(data);
      } else {
        // Iter 85.75 · Send whichever fields the caller is entitled to
        // change. Self-edits always include Name, Capacity, Email, Role
        // (backend has been relaxed to accept all four on your own
        // record). Editing another member still requires partner-tier.
        const patch = { name, capacity_hours_per_week: Number(form.capacity_hours_per_week) || 40 };
        if (canEditOwnEmail) {
          patch.email = form.email.trim().toLowerCase();
        }
        if (canEditOwnRole) {
          patch.role = form.role;
        }
        const { data } = await api.put(`/users/${member.id}`, patch);
        toast.success("Team member updated");
        onOpenChange(false);
        onSaved?.(data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit
    ? (isSelf ? "Edit your info" : `Edit ${member?.name || "team member"}`)
    : "Add team member";

  const canEditRoleEmail = canManage;
  const emailDisabled = isEdit && !canEditOwnEmail;
  const roleSelectDisabled = isEdit && !canEditOwnRole;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-lg" data-testid="team-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Name</div>
            <Input
              data-testid="team-member-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-none bg-secondary/40"
            />
          </div>
          <div>
            <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Email</div>
            <Input
              data-testid="team-member-email"
              type="email"
              disabled={emailDisabled}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-none bg-secondary/40 disabled:opacity-60"
            />
            {emailDisabled && (
              <div className="mt-1 text-[0.6875rem] text-muted-foreground">Only Partners can change another member's email.</div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Role</div>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
                disabled={roleSelectDisabled}
              >
                <SelectTrigger data-testid="team-member-role" className="rounded-none bg-secondary/40 disabled:opacity-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              {roleSelectDisabled && (
                <div className="mt-1 text-[0.6875rem] text-muted-foreground">Only Partners can change another member's role.</div>
              )}
            </div>
            <div>
              <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Capacity (h/wk)</div>
              <Input
                data-testid="team-member-capacity"
                type="number"
                min={0}
                max={80}
                value={form.capacity_hours_per_week}
                onChange={(e) => setForm({ ...form, capacity_hours_per_week: e.target.value })}
                className="rounded-none bg-secondary/40"
              />
            </div>
          </div>
          {!isEdit && (
            <div>
              <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Temporary password</div>
              <Input
                data-testid="team-member-password"
                type="text"
                placeholder="Share out-of-band; invitee rotates on first sign-in"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-none bg-secondary/40"
              />
              <div className="mt-1 text-[0.6875rem] text-muted-foreground">Minimum 8 characters.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            data-testid="team-member-save"
            onClick={commit}
            disabled={saving}
            className="rounded-none bg-primary text-primary-foreground"
          >
            {saving ? "Saving…" : (isEdit ? "Save" : "Add member")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
